import { Temporal } from "@js-temporal/polyfill";
import { useEffect, useRef, useState } from "react";
import { Check, Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button.jsx";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip.jsx";
import {
  copyTextToClipboard,
  downloadTextFile,
} from "@/lib/clipboard-utils.js";
import { cn } from "@/lib/utils.js";

const ICON_BUTTON_CLASS =
  "h-8 w-8 p-0 font-light text-sm border border-brand-cyan/50 text-brand-cyan hover:bg-brand-cyan/10 hover:!text-brand-cyan hover:[&_svg]:!text-brand-cyan transition-colors dark:border-brand-cyan/60 dark:hover:bg-brand-cyan/20";

/**
 * @param {string} value
 * @returns {string}
 */
function sanitizeFileNamePart(value) {
  return (
    String(value)
      .trim()
      .replace(/[/\\:*?"<>|]/g, "-")
      .replace(/\s+/g, "_") || "unknown"
  );
}

/**
 * @param {string} isoDateTime
 * @returns {string}
 */
function formatShutdownDateTimeForFileName(isoDateTime) {
  const s = isoDateTime.trim();
  if (!s) {
    return "analysis";
  }

  const pad = (n) => String(n).padStart(2, "0");
  try {
    const instant = Temporal.Instant.from(s);
    const zdt = instant.toZonedDateTimeISO(Temporal.Now.timeZoneId());
    return `${zdt.year}${pad(zdt.month)}${pad(zdt.day)}${pad(zdt.hour)}${pad(zdt.minute)}${pad(zdt.second)}`;
  } catch {
    const ms = Date.parse(s);
    if (!Number.isFinite(ms)) {
      return "analysis";
    }
    const d = new Date(ms);
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  }
}

/**
 * @param {string} assetNumber
 * @param {string} startDate
 * @returns {string}
 */
function buildDownloadFileName(assetNumber, startDate) {
  const assetPart = sanitizeFileNamePart(assetNumber);
  const dateTimePart = formatShutdownDateTimeForFileName(startDate);
  return `rca-ai-analysis-${assetPart}-${dateTimePart}.txt`;
}

/**
 * @param {{
 *   label: string,
 *   onClick: () => void,
 *   children: import("react").ReactNode,
 *   className?: string,
 * }} props
 */
function ActionIconButton({ label, onClick, children, className }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className={cn(ICON_BUTTON_CLASS, className)}
          onClick={onClick}
          aria-label={label}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side='top' sideOffset={6}>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Copy and download RCA AI analysis as plain text.
 *
 * @param {{
 *   contentRef: import("react").RefObject<HTMLElement|null>,
 *   assetNumber: string,
 *   startDate: string,
 *   className?: string,
 * }} props
 */
export default function RcaAiAnalysisActions({
  contentRef,
  assetNumber,
  startDate,
  className,
}) {
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current != null) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  function getPlainText() {
    return contentRef.current?.innerText.trim() ?? "";
  }

  async function handleCopy() {
    const success = await copyTextToClipboard(getPlainText());
    if (!success) {
      return;
    }

    setCopied(true);
    if (copiedTimeoutRef.current != null) {
      clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = setTimeout(() => {
      copiedTimeoutRef.current = null;
      setCopied(false);
    }, 2000);
  }

  function handleDownload() {
    downloadTextFile(
      getPlainText(),
      buildDownloadFileName(assetNumber, startDate)
    );
  }

  return (
    <div className={cn("flex justify-end gap-1", className)}>
      <ActionIconButton
        label={copied ? "Copied" : "Copy as plain text"}
        onClick={handleCopy}>
        {copied ? (
          <Check className='h-4 w-4' aria-hidden />
        ) : (
          <Copy className='h-4 w-4' aria-hidden />
        )}
      </ActionIconButton>
      <ActionIconButton label='Download text file' onClick={handleDownload}>
        <Download className='h-4 w-4' aria-hidden />
      </ActionIconButton>
    </div>
  );
}
